import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';
import { businessInfo } from '../mockData';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const categories = [
    { name: "Silver Jewellery", slug: "silver-jewellery" },
    { name: "Silver Idols & Deities", slug: "silver-idols" },
    { name: "Puja Accessories", slug: "puja-accessories" },
    { name: "Silver Utensils & Serveware", slug: "silver-utensils" },
    { name: "Gifting Items", slug: "gifting-items" },
    { name: "Customised Orders", slug: "customised-orders", isCustom: true }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const goToHomeSection = (sectionId) => {
    if (location.pathname === '/') {
      scrollToSection(sectionId);
    } else {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
    setIsMobileMenuOpen(false);
  };

  const handleCategoryClick = (cat) => {
    setIsProductsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    setIsMobileProductsOpen(false);
    if (cat.isCustom) {
      navigate('/customised-orders');
    } else {
      navigate(`/shop/${cat.slug}`);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'nav-scrolled' : 'nav-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_kothari-jewels/artifacts/vies131i_logo.jpg" 
              alt="Shri Kothari Jewellers" 
              className="h-14 w-14 rounded-full object-cover logo-glow"
            />
            <span className="text-xl font-bold text-gold-primary hidden sm:block">
              {businessInfo.name}
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => goToHomeSection('about')} className="nav-link" data-testid="nav-about">About</button>
            
            {/* Products Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsProductsDropdownOpen(true)}
              onMouseLeave={() => setIsProductsDropdownOpen(false)}
            >
              <button 
                className="nav-link"
                onClick={() => goToHomeSection('categories')}
                data-testid="nav-products"
              >
                Products
              </button>
              
              {isProductsDropdownOpen && (
                <div 
                  className="absolute top-full left-0 pt-3 w-72 z-50"
                  data-testid="nav-products-dropdown"
                >
                  <div 
                    className="rounded-lg py-2 border"
                    style={{
                      background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)',
                      borderColor: 'rgba(212, 175, 55, 0.4)',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(212, 175, 55, 0.15)'
                    }}
                  >
                    {categories.map((cat) => (
                      <button
                        key={cat.slug}
                        onClick={() => handleCategoryClick(cat)}
                        data-testid={`nav-products-item-${cat.slug}`}
                        className="w-full text-left px-5 py-3 transition-all duration-200 block border-l-2 border-transparent hover:border-l-2"
                        style={{
                          color: cat.isCustom ? '#F4D03F' : '#E8E6E3',
                          fontFamily: "'Cinzel', serif",
                          fontSize: '0.95rem',
                          letterSpacing: '0.03em'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#D4AF37';
                          e.currentTarget.style.background = 'rgba(212, 175, 55, 0.08)';
                          e.currentTarget.style.borderLeftColor = '#D4AF37';
                          e.currentTarget.style.paddingLeft = '1.5rem';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = cat.isCustom ? '#F4D03F' : '#E8E6E3';
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.borderLeftColor = 'transparent';
                          e.currentTarget.style.paddingLeft = '1.25rem';
                        }}
                      >
                        {cat.isCustom && <span style={{marginRight: '8px'}}>&#10022;{' '}</span>}
                        <span>{cat.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <button onClick={() => goToHomeSection('contact')} className="nav-link" data-testid="nav-contact">Contact</button>
            
            {/* Contact Dropdown */}
            <div 
              className="relative contact-dropdown-wrapper"
              onMouseEnter={() => setIsContactDropdownOpen(true)}
              onMouseLeave={() => setIsContactDropdownOpen(false)}
            >
              <button className="btn-gold flex items-center space-x-2">
                <Phone size={18} />
                <span>Get in Touch</span>
                <svg 
                  className={`w-4 h-4 transition-transform ${isContactDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isContactDropdownOpen && (
                <div className="contact-dropdown">
                  <a 
                    href={`tel:${businessInfo.phone}`}
                    className="contact-dropdown-item"
                    onClick={(e) => {
                      setIsContactDropdownOpen(false);
                    }}
                  >
                    <Phone size={18} />
                    <span>Call Us</span>
                  </a>
                  <a 
                    href={`https://wa.me/${businessInfo.whatsappNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-dropdown-item"
                    onClick={(e) => {
                      setIsContactDropdownOpen(false);
                    }}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    <span>WhatsApp Chat</span>
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-gold-primary"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden mobile-menu">
          <div className="px-4 pt-2 pb-4 space-y-3">
            <button onClick={() => goToHomeSection('about')} className="mobile-nav-link" data-testid="mobile-nav-about">About</button>
            
            {/* Mobile Products Expandable */}
            <div>
              <button 
                onClick={() => {
                  setIsMobileProductsOpen(!isMobileProductsOpen);
                  goToHomeSection('categories');
                }}
                className="mobile-nav-link w-full text-left flex items-center justify-between"
                data-testid="mobile-nav-products"
              >
                <span>Products</span>
                <span style={{color: '#D4AF37', fontSize: '0.85rem'}}>
                  {isMobileProductsOpen ? '−' : '+'}
                </span>
              </button>
              {isMobileProductsOpen && (
                <div 
                  className="mt-2 ml-2 rounded-lg py-1 border"
                  style={{
                    background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)',
                    borderColor: 'rgba(212, 175, 55, 0.3)'
                  }}
                  data-testid="mobile-nav-products-dropdown"
                >
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategoryClick(cat)}
                      data-testid={`mobile-nav-products-item-${cat.slug}`}
                      className="w-full text-left px-4 py-2.5 block transition-colors"
                      style={{
                        color: cat.isCustom ? '#F4D03F' : '#E8E6E3',
                        fontFamily: "'Cinzel', serif",
                        fontSize: '0.9rem',
                        letterSpacing: '0.03em'
                      }}
                    >
                      {cat.isCustom && <span style={{marginRight: '6px'}}>&#10022;{' '}</span>}
                      <span>{cat.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <button onClick={() => goToHomeSection('contact')} className="mobile-nav-link" data-testid="mobile-nav-contact">Contact</button>
            <a 
              href={`tel:${businessInfo.phone}`}
              className="btn-gold flex items-center justify-center space-x-2 w-full"
            >
              <Phone size={18} />
              <span>Call Us</span>
            </a>
            <a 
              href={`https://wa.me/${businessInfo.whatsappNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold flex items-center justify-center space-x-2 w-full bg-green-600"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span>WhatsApp Chat</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
